import type React from 'react';
import { useEffect } from 'react';

type UseDrawerFocusTrapOptions = {
  drawerRef: React.RefObject<HTMLDivElement | null>;
  motionState: 'opening' | 'closing';
  isMobile: boolean;
  closeDrawer: () => void;
};

export function useDrawerFocusTrap({ drawerRef, motionState, isMobile, closeDrawer }: UseDrawerFocusTrapOptions) {
  useEffect(() => {
    const drawer = drawerRef.current;
    const isOpen = motionState === 'opening';

    if (!drawer || !isOpen) return;

    const getActiveElement = (element: HTMLElement): Element | null => {
      const root = element.getRootNode();

      if (root instanceof ShadowRoot) return root.activeElement;

      return element.ownerDocument.activeElement;
    };

    const getFocusableElements = () =>
      Array.from(
        drawer.querySelectorAll<HTMLElement>(
          'button:not([disabled]):not([tabindex="-1"]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );

    const handleKeyDown = (event: KeyboardEvent) => {
      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.key === 'Escape') {
        event.preventDefault();
        if (isMobile) {
          lastElement.focus();
          closeDrawer();
        }
        return;
      }
      if (event.key !== 'Tab' || motionState !== 'opening') return;

      const activeElement = getActiveElement(drawer);

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    drawer.focus();
    drawer.addEventListener('keydown', handleKeyDown);
    return () => drawer.removeEventListener('keydown', handleKeyDown);
  }, [closeDrawer, drawerRef, isMobile, motionState]);
}
